/**
 * As peças visuais que as 11 sub-abas do Sistema repetem, cada uma com a classe do legado que ela
 * substitui ao lado. Ficam aqui, e não em `src/comum/`, porque só esta área as usa; o que serve ao
 * admin inteiro (confirmação, status, moeda, copiar, rádio) já vem de `comum/`.
 *
 * Nenhuma cor literal: tudo sai dos tokens do preset (ADR 0004), e o alvo de toque de 44 px vem do
 * `min-h-toque` do Chassi.
 */

// O tipo do conteúdo, a referência, o efeito e o identificador único do React.
import { forwardRef, useEffect, useId, useRef, type HTMLAttributes, type ReactNode } from 'react';
// O botão da plataforma: o produto não desenha botão.
import { Botao } from '@complexity/ui';
// A memória da área, onde o arquivo escolhido espera a volta.
import { useMemoria } from './memoria.js';

/**
 * O estado de uma lista carregada do banco, com os três momentos que o legado desenha no lugar
 * dela: "Carregando…", "Erro ao carregar: " com a mensagem, e a lista pronta.
 */
export type Carga<T> =
  | { readonly tipo: 'carregando' }
  | { readonly tipo: 'erro'; readonly mensagem: string }
  | { readonly tipo: 'pronta'; readonly dados: T };

/** O que toda sub-aba recebe da tela: a confirmação e o aviso do legado, e o sinal de recarga. */
export interface PropsDaSubAba {
  // `mostrarConfirmacao` do legado: `true` só no OK.
  readonly confirmar: (mensagem: string) => Promise<boolean>;
  // `mostrarAlerta` do legado: título "Aviso" e o botão "Entendi".
  readonly alertar: (mensagem: string) => Promise<boolean>;
  // Muda a cada clique no botão da sub-aba, que no legado recarrega a sub-aba (`admin.html:6478`).
  readonly recarga: number;
}

/**
 * O cabeçalho de colunas de um cartão (`.param-card-col-header`, `admin.html:1163` e `1649`): some no
 * celular, onde cada linha vira cartão empilhado, e volta no tablet, a partir de 768 px, como no
 * legado. `recuoDeAlca` reserva a coluna da alça de arrastar nos cartões que têm alça.
 */
export function CabecalhoDeColunas({ children, recuoDeAlca }: { readonly children: ReactNode; readonly recuoDeAlca?: boolean }) {
  return (
    <div className="hidden items-center gap-cx-3 border-b border-borda bg-superficie-afundada px-cx-4 py-cx-2 text-cx-xs font-semibold uppercase tracking-wide text-texto-suave md:flex">
      {/* O recuo fantasma de 22 px da alça, como o `::before` do legado (`admin.html:1657`). */}
      {recuoDeAlca && <span aria-hidden="true" className="w-[22px] shrink-0" />}
      {children}
    </div>
  );
}

/** A nota explicativa do topo de cada sub-aba (`.nota-secao`, `admin.html:1931`). */
export function NotaSecao({ children }: { readonly children: ReactNode }) {
  return <p className="text-cx-sm text-texto-suave">{children}</p>;
}

/**
 * O trecho em destaque de alerta dentro de uma nota (`.destaque-alerta`, `admin.html:1434`): o
 * legado o pinta com `--erro`, e aqui a mesma cor sai do token `erro`.
 */
export function DestaqueAlerta({ children }: { readonly children: ReactNode }) {
  return <strong className="text-erro">{children}</strong>;
}

/** O texto de estado da lista: "Carregando…", o vazio e o erro de leitura (`.param-vazio`). */
export function TextoParam({ children, testid }: { readonly children: ReactNode; readonly testid?: string }) {
  return (
    <p className="text-cx-sm text-texto-suave [overflow-wrap:anywhere]" data-testid={testid}>
      {children}
    </p>
  );
}

/**
 * O cartão que agrupa as linhas (`.param-card`, `admin.html:1146`): borda, raio e fundo dos
 * tokens, e o conteúdo cortado no raio, como o `overflow:hidden` do legado.
 */
export const CartaoParam = forwardRef<HTMLDivElement, { readonly children: ReactNode; readonly testid?: string }>(
  function CartaoParam({ children, testid }, ref) {
    return (
      <div
        ref={ref}
        data-testid={testid}
        className="overflow-hidden rounded-cx-cartao border border-borda bg-superficie"
      >
        {children}
      </div>
    );
  },
);

/**
 * O cabeçalho de produto do cartão (`.param-card-header`, `admin.html:1147-1159`): fundo da marca,
 * título em caixa alta e o selo de contagem à direita, quebrando linha quando não cabe.
 */
export function CabecalhoDeProduto({ titulo, selo }: { readonly titulo: string; readonly selo: string }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-cx-3 bg-marca px-cx-4 py-cx-3 text-marca-contraste">
      <span className="text-cx-xs font-semibold uppercase tracking-wide [overflow-wrap:anywhere]">{titulo}</span>
      <span className="shrink-0 whitespace-nowrap rounded-cx-sm bg-marca-forte px-cx-2 py-cx-1 text-cx-xs font-semibold uppercase tracking-wide">
        {selo}
      </span>
    </div>
  );
}

/**
 * Uma linha do cartão (`.param-card-row`, `admin.html:1164`): itens quebrando linha, divisória
 * entre linhas e nenhuma depois da última. `extra` leva os atributos de arraste quando existem.
 */
export function LinhaParam({
  children,
  className = '',
  ...extra
}: { readonly children: ReactNode; readonly className?: string } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      // No tablet a linha deixa de quebrar e centraliza, como `.param-card-row` a partir de 768 px.
      className={`flex flex-wrap items-start gap-cx-3 border-b border-borda px-cx-4 py-cx-3 last:border-b-0 md:flex-nowrap md:items-center ${className}`}
      {...extra}
    >
      {children}
    </div>
  );
}

/**
 * A alça de arrastar (`.param-card-arraste`, `admin.html:1173-1178`), com a dica "Arrastar para
 * reordenar" do legado.
 *
 * O DESENHO MUDA DE PROPÓSITO, e só ele: o legado escreve dois caracteres Braille (U+283F, duas vezes) e a regra
 * da casa não deixa símbolo Unicode entrar na tela, porque o desenho dele muda por sistema e por
 * fonte. Aqui são os mesmos 4 x 3 pontos, em SVG, na cor `texto-fraco` (o `--muted` do legado). O
 * `touch-action: none` fica, porque é ele que deixa o dedo arrastar sem rolar a página.
 */
export function AlcaDeArraste() {
  // As colunas e linhas dos pontos: dois U+283F lado a lado são 4 colunas de 3 pontos.
  const colunas = [3, 7, 13, 17];
  const linhas = [4, 10, 16];
  return (
    <span
      title="Arrastar para reordenar"
      data-testid="alca-arraste"
      className="flex w-[22px] shrink-0 cursor-grab touch-none select-none items-center justify-center py-cx-1 text-texto-fraco active:cursor-grabbing"
    >
      <svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        {colunas.flatMap((x) => linhas.map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" />))}
      </svg>
    </span>
  );
}

/**
 * O botão de ação da linha (`.param-remover`, `admin.html:1208-1215`): texto pequeno em caixa alta,
 * sublinhado, cinza, e vermelho ao passar o mouse. É o "Salvar", "Ativar" e "Desativar" das listas.
 *
 * `perigo` PINTA DE VERMELHO DESDE O REPOUSO, e não só no hover: decisão do fundador de 19/09/2026,
 * "toda ação de APAGAR/EXCLUIR/REMOVER é VERMELHA em todo lugar". É a mesma tinta que o legado já usa
 * no "Excluir" do Manual Foto (`a.excluir-link`, `admin.html:480`, `color: var(--erro)`), aqui pelo
 * token `erro`. O `!` vence a cor da variante `fantasma` do Chassi, que é `text-texto-suave`.
 * Ficam vermelhos: "Excluir" e "Remover". O hover continua vermelho nos outros, como no legado.
 */
export function BotaoDaLinha({
  children,
  onClick,
  testid,
  perigo,
}: {
  readonly children: ReactNode;
  readonly onClick: () => void;
  readonly testid?: string;
  readonly perigo?: boolean;
}) {
  return (
    <Botao
      variante="fantasma"
      tamanho="sm"
      data-testid={testid}
      data-perigo={perigo ? 'sim' : undefined}
      className={`uppercase tracking-wide underline underline-offset-4 ${perigo ? '!text-erro' : 'hover:text-erro'}`}
      onClick={onClick}
    >
      {children}
    </Botao>
  );
}

/**
 * O campo de arquivo do legado (`input type="file"` com `<label>`, `admin.html:2064` e `2086`).
 * O Chassi não tem peça de arquivo genérico (o `CampoAnexo` é só de imagem e reduz o que recebe),
 * então este é o `input` nativo do legado, com o rótulo visível ligado a ele e a letra do Chassi.
 *
 * SEM MARCA DE OBRIGATÓRIO: o markup do legado não tem asterisco nem `required` em nenhum campo das
 * 11 sub-abas (`admin.html:1930-2193`, medido na verificação de 19/09/2026, divergência D5), e quem
 * recusa o envio vazio é o clique, com o texto do legado ("Escolhe um arquivo primeiro.").
 *
 * O ARQUIVO ESCOLHIDO MORA NA MEMÓRIA DA ÁREA (`chave`), e não só no `input`: no legado o `input`
 * fica no DOM da página e conserva o arquivo quando a pessoa vai a outra aba e volta. Ao montar, o
 * arquivo guardado volta para o `input` por um `DataTransfer`, que é o único jeito de devolver um
 * `FileList` a um campo de arquivo.
 */
export function CampoArquivo({
  rotulo,
  chave,
  accept,
}: {
  readonly rotulo: string;
  readonly chave: string;
  readonly accept?: string;
}) {
  // O `for` do rótulo precisa de um id único na página.
  const id = useId();
  // O arquivo guardado na memória da área, e o `input` desta montagem.
  const [guardado, guardar] = useMemoria<File | null>(chave, null);
  const campo = useRef<HTMLInputElement | null>(null);
  // Põe (ou tira) no `input` o que está na memória, inclusive quando outro gesto a limpou depois de
  // a tela já ter saído (o envio que terminou fora desta montagem).
  useEffect(() => {
    const input = campo.current;
    if (!input) return;
    if (!guardado) {
      if (input.value) input.value = '';
      return;
    }
    if (input.files?.[0] === guardado) return;
    const bandeja = new DataTransfer();
    bandeja.items.add(guardado);
    input.files = bandeja.files;
  }, [guardado]);
  return (
    <div className="flex flex-col gap-cx-1">
      <label htmlFor={id} className="text-cx-sm font-medium text-texto">
        {rotulo}
      </label>
      <input
        id={id}
        ref={campo}
        type="file"
        accept={accept}
        className="min-h-toque max-w-full text-cx-sm text-texto"
        onChange={(evento) => guardar(evento.target.files?.[0] ?? null)}
      />
    </div>
  );
}
